import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocateFixed, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import { SkeletonBlock } from '../components/Skeleton';
import usePageMetadata from '../hooks/usePageMetadata';
import useCurrentLocation from '../hooks/useCurrentLocation';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import * as addressesApi from '../api/endpoints/addresses';
import * as configApi from '../api/endpoints/config';
import * as ordersApi from '../api/endpoints/orders';
import { RAZORPAY_CHECKOUT_SRC } from '../config/api';
import { containerNarrow, focusRing, inputClass, pageHeading, pageSection, panelHeading } from '../styles/ui';

// Human-readable messages for the documented order-placement error codes.
const ERROR_MESSAGES = {
  PROFILE_INCOMPLETE: 'Please complete your name, phone and a delivery address before placing an order.',
  PINCODE_UNSERVICEABLE: "We don't deliver to this pincode yet. Please try a different address.",
  DELIVERY_COORDINATES_REQUIRED: 'Please choose a pinned delivery location for this address.',
  PRODUCT_NOT_FOUND: 'Some items in your cart are no longer available.',
  CATCH_WEIGHT_REQUIRES_COD: 'One or more items have variable weight and must be paid on delivery. Switching to Cash on Delivery.',
  PRICE_UNAVAILABLE: 'Some items are not currently available for online checkout.',
  COD_NOT_AVAILABLE: 'Cash on Delivery is not available for this order.',
  STOCK_CHANGED: 'Stock changed for one or more items. Please review your cart.',
  DELIVERY_SLOTS_UNAVAILABLE: 'Delivery slots are unavailable between 12:00 AM and 6:00 AM. Please come back after 6:00 AM to place your order.',
  DELIVERY_SLOT_REQUIRED: 'Please select an available delivery slot.',
  INVALID_DELIVERY_SLOT: 'Please select an available delivery slot.',
  NO_DELIVERY_SLOTS_AVAILABLE: 'No delivery slots are currently available. Please try again later.',
  DELIVERY_SLOT_EXPIRED: 'This delivery slot is no longer available. Please choose the next available slot.',
};

// Selectable radio-card shared by the address and payment-method lists, so
// both render an identical box in every state instead of each list
// hand-rolling its own selected/disabled styling.
const optionCardClass = (selected, disabled = false) =>
  `flex items-center gap-3 p-4 rounded-xl border transition-colors ${
    disabled
      ? 'border-emerald-100 bg-canvas opacity-60 cursor-not-allowed'
      : `cursor-pointer ${
          selected ? 'border-emerald-700 bg-emerald-50' : 'border-emerald-100 hover:bg-emerald-50/60'
        }`
  }`;

let razorpayScriptPromise = null;
const loadRazorpayScript = () => {
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load payment gateway.'));
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
};

const CheckoutPage = () => {
  const { items, clearCart } = useCart();
  const { customer } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newAddress, setNewAddress] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [deliverySlots, setDeliverySlots] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [placing, setPlacing] = useState(false);
  const { locating, locationError, captureLocation } = useCurrentLocation();

  const idempotencyKeyRef = useRef(null);
  const getIdempotencyKey = () => {
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();
    return idempotencyKeyRef.current;
  };
  const resetIdempotencyKey = () => { idempotencyKeyRef.current = null; };

  usePageMetadata({
    title: 'Checkout | Fresh & Homely',
    description: 'Complete your Fresh & Homely order.',
    canonicalPath: '/checkout',
  });

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart', { replace: true });
      return;
    }
    Promise.all([addressesApi.listAddresses(), configApi.getPaymentSettings(), configApi.getDeliverySlots()])
      .then(([addressRes, paymentRes, slotsRes]) => {
        const list = addressRes.data || [];
        setAddresses(list);
        const defaultAddr = list.find((a) => a.isDefault) || list[0];
        if (defaultAddr) setSelectedAddressId(defaultAddr._id);
        setPaymentSettings(paymentRes.data);
        setDeliverySlots(slotsRes.data);
        const firstAvailable = slotsRes.data?.slots?.find((slot) => slot.available);
        if (firstAvailable) setSelectedSlotId(firstAvailable.id);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCatchWeightItem = useMemo(() => items.some((item) => item.product?.catchWeight), [items]);
  const effectivePaymentMethod = hasCatchWeightItem ? 'cod' : paymentMethod;

  const selectedAddress = addresses?.find((a) => a._id === selectedAddressId);

  const handleAddressField = (field) => (event) => {
    setNewAddress((prev) => ({ ...(prev || {}), [field]: event.target.value }));
    resetIdempotencyKey();
  };

  const handlePinLocation = async () => {
    const coordinates = await captureLocation();
    if (coordinates) {
      setNewAddress((prev) => ({ ...(prev || {}), coordinates }));
      resetIdempotencyKey();
    }
  };

  const buildDeliveryAddress = () => {
    if (selectedAddressId && selectedAddress) {
      return {
        addressId: selectedAddress._id,
        line1: selectedAddress.line1,
        line2: selectedAddress.line2 || '',
        city: selectedAddress.city,
        state: selectedAddress.state,
        pincode: selectedAddress.pincode,
        landmark: selectedAddress.landmark || '',
        coordinates: selectedAddress.coordinates,
      };
    }
    return newAddress;
  };

  const handlePlaceOrder = async () => {
    setError(null);
    const deliveryAddress = buildDeliveryAddress();
    if (!deliveryAddress?.coordinates) {
      setError('Please pin a delivery location for this address before placing your order.');
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        items: items.map((item) => ({ productId: item.productId, quantity: item.quantity })),
        deliveryAddress,
        paymentMethod: effectivePaymentMethod,
        deliverySlotId: selectedSlotId,
      };

      const response = await ordersApi.placeOrder(payload, getIdempotencyKey());
      const { data } = response;

      if (data.paymentMethod === 'cod' || data.paymentMethod === 'wallet') {
        // Both are already fully settled server-side with no Razorpay order to open.
        clearCart();
        navigate(`/order-confirmation/${data.saleId}`);
        return;
      }

      // Online payment: open Razorpay Checkout with the values the backend returned.
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: data.razorpayKeyId,
        order_id: data.razorpayOrderId,
        amount: data.amount,
        currency: data.currency,
        name: 'Fresh & Homely',
        prefill: { name: customer?.name, email: customer?.email, contact: customer?.phone },
        handler: async (paymentResponse) => {
          try {
            const confirmRes = await ordersApi.confirmPayment(
              data.saleId,
              paymentResponse.razorpay_payment_id,
              paymentResponse.razorpay_signature
            );
            if (confirmRes.data?.status === 'refunded') {
              setError('Your payment was refunded and the order was not placed. Please try again.');
              showToast('Payment was refunded — order not placed', { tone: 'error' });
              setPlacing(false);
              return;
            }
            clearCart();
            navigate(`/order-confirmation/${data.saleId}`);
          } catch (confirmErr) {
            // The callback can occasionally fail to register a successful charge;
            // poll payment-status once before surfacing an error to the user.
            try {
              const statusRes = await ordersApi.getPaymentStatus(data.saleId);
              if (statusRes.data?.paymentStatus === 'completed') {
                clearCart();
                navigate(`/order-confirmation/${data.saleId}`);
                return;
              }
            } catch {
              // fall through to error display
            }
            setError('Payment confirmation failed. Please check your order history before retrying.');
            showToast('Payment confirmation failed', { tone: 'error' });
            setPlacing(false);
          }
        },
        modal: {
          ondismiss: () => setPlacing(false),
        },
      });
      rzp.open();
    } catch (err) {
      if (err.code === 'CATCH_WEIGHT_REQUIRES_COD') {
        setPaymentMethod('cod');
        resetIdempotencyKey();
      }
      setError(ERROR_MESSAGES[err.code] || err.message);
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="py-24 min-h-[60vh] bg-gradient-to-b from-white to-canvas">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SkeletonBlock className="h-10 w-48 mb-10" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6 mb-6 space-y-3">
                <SkeletonBlock className="h-6 w-40" />
                <SkeletonBlock className="h-16 w-full" />
              </div>
            ))}
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerNarrow}>
          <h1 className={`${pageHeading} mb-8 sm:mb-10`}>Checkout</h1>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6 mb-6">
            <h2 className={`${panelHeading} mb-4`}>Delivery Address</h2>

            {addresses && addresses.length > 0 && (
              <div className="space-y-3 mb-4">
                {addresses.map((address) => {
                  const selected = selectedAddressId === address._id;
                  return (
                    <label key={address._id} className={optionCardClass(selected)}>
                      <input
                        type="radio"
                        name="address"
                        className="sr-only"
                        checked={selected}
                        onChange={() => { setSelectedAddressId(address._id); setNewAddress(null); resetIdempotencyKey(); }}
                      />
                      <span
                        className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selected ? 'border-emerald-700 bg-emerald-700' : 'border-emerald-200'
                        }`}
                        aria-hidden="true"
                      >
                        {selected && <span className="w-2 h-2 rounded-full bg-white" />}
                      </span>
                      <div>
                        <p className="font-medium text-emerald-950">{address.label || 'Address'}</p>
                        <p className="text-sm text-ink-secondary">
                          {[address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <details
              className="mt-2"
              open={!addresses || addresses.length === 0}
              onToggle={(e) => { if (e.target.open) { setSelectedAddressId(null); setNewAddress(newAddress || {}); } }}
            >
              <summary className={`cursor-pointer text-sm font-medium text-emerald-700 ${focusRing}`}>
                Use a new address
              </summary>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Address line 1" className={`sm:col-span-2 ${inputClass}`} value={newAddress?.line1 || ''} onChange={handleAddressField('line1')} />
                <input placeholder="Address line 2 (optional)" className={`sm:col-span-2 ${inputClass}`} value={newAddress?.line2 || ''} onChange={handleAddressField('line2')} />
                <input placeholder="City" className={inputClass} value={newAddress?.city || ''} onChange={handleAddressField('city')} />
                <input placeholder="State" className={inputClass} value={newAddress?.state || ''} onChange={handleAddressField('state')} />
                <input placeholder="Pincode" className={inputClass} value={newAddress?.pincode || ''} onChange={handleAddressField('pincode')} />
                <input placeholder="Landmark (optional)" className={inputClass} value={newAddress?.landmark || ''} onChange={handleAddressField('landmark')} />
              </div>
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handlePinLocation}
                  disabled={locating}
                  className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition-colors disabled:opacity-60 ${
                    newAddress?.coordinates ? 'border-emerald-700 bg-emerald-50 text-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                  } ${focusRing}`}
                >
                  <LocateFixed size={16} aria-hidden="true" />
                  {locating ? 'Locating...' : newAddress?.coordinates ? 'Location pinned' : 'Pin my current location'}
                </button>
              </div>
              {locationError && <p className="text-xs text-red-600 mt-2">{locationError}</p>}
              <p className="text-xs text-ink-secondary mt-2">
                A pinned location is required for delivery tracking.
              </p>
            </details>
          </div>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6 mb-6">
            <h2 className={`${panelHeading} mb-4`}>Payment Method</h2>
            <div className="space-y-3">
              <label className={optionCardClass(effectivePaymentMethod === 'cod')}>
                <input type="radio" name="paymentMethod" className="sr-only" checked={effectivePaymentMethod === 'cod'} onChange={() => { setPaymentMethod('cod'); resetIdempotencyKey(); }} />
                <span
                  className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    effectivePaymentMethod === 'cod' ? 'border-emerald-700 bg-emerald-700' : 'border-emerald-200'
                  }`}
                  aria-hidden="true"
                >
                  {effectivePaymentMethod === 'cod' && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <span className="font-medium text-emerald-950">Cash on Delivery</span>
              </label>
              {paymentSettings?.allowCashOnDelivery === false && (
                <p className="text-xs text-amber-700 -mt-2">Cash on Delivery is currently unavailable.</p>
              )}
              <label className={optionCardClass(effectivePaymentMethod === 'online', hasCatchWeightItem)}>
                <input
                  type="radio"
                  name="paymentMethod"
                  className="sr-only"
                  disabled={hasCatchWeightItem}
                  checked={effectivePaymentMethod === 'online'}
                  onChange={() => { setPaymentMethod('online'); resetIdempotencyKey(); }}
                />
                <span
                  className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    effectivePaymentMethod === 'online' ? 'border-emerald-700 bg-emerald-700' : 'border-emerald-200'
                  }`}
                  aria-hidden="true"
                >
                  {effectivePaymentMethod === 'online' && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
                <span className="font-medium text-emerald-950">Pay Online</span>
              </label>
              {hasCatchWeightItem && (
                <p className="text-xs text-amber-700 -mt-2">
                  Your cart has a weight-variable item, so online payment isn't available — this order will be Cash on Delivery.
                </p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-emerald-50 shadow-card p-6 mb-6">
            <h2 className={`${panelHeading} mb-4`}>Delivery Slot</h2>
            {deliverySlots?.orderingAvailable === false && (
              <p className="text-sm text-amber-700">
                {deliverySlots.unavailableReason?.message || 'Delivery slots are currently unavailable.'}
              </p>
            )}
            {deliverySlots?.orderingAvailable && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {deliverySlots.slots.map((slot) => {
                  const selected = selectedSlotId === slot.id;
                  return (
                    <label
                      key={slot.id}
                      className={`flex items-center justify-center gap-1.5 text-center p-3 rounded-xl border text-sm font-medium transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-emerald-700 has-[:focus-visible]:outline-offset-2 ${
                        !slot.available
                          ? 'opacity-50 cursor-not-allowed border-emerald-100 text-gray-400'
                          : selected
                            ? 'border-emerald-700 bg-emerald-50 text-emerald-950 ring-1 ring-emerald-700 cursor-pointer'
                            : 'border-emerald-100 text-emerald-950 hover:border-emerald-200 cursor-pointer'
                      }`}
                    >
                      <input
                        type="radio"
                        name="deliverySlot"
                        className="sr-only"
                        disabled={!slot.available}
                        checked={selected}
                        onChange={() => { setSelectedSlotId(slot.id); resetIdempotencyKey(); }}
                      />
                      {selected && <CheckCircle2 size={14} className="text-emerald-700 shrink-0" aria-hidden="true" />}
                      {slot.label}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

          <Button
            size="lg"
            onClick={handlePlaceOrder}
            disabled={placing || (!selectedAddressId && !newAddress?.line1) || !selectedSlotId}
            loading={placing}
            loadingLabel="Placing order..."
            className="w-full"
          >
            Place Order
          </Button>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CheckoutPage;
